import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Calendar } from '@/shared/components/ui/Calendar';
import { TimeSelect } from '@/shared/components/ui/TimeSelect';
import { ToggleSwitch } from '@/shared/components/custom/toggle-switch/ToggleSwitch';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { formatDisplayDate } from '@/shared/utils/dateUtils';

export interface RequestDayFieldValue {
  date: string;
  isFullDay: boolean;
  startTime?: string;
  endTime?: string;
}

interface DateRangeWithDailyTimeFieldProps {
  value: RequestDayFieldValue[];
  onChange: (days: RequestDayFieldValue[]) => void;
}

/**
 * Date range picker (built on the existing `Calendar` in `mode="range"` —
 * no new range-selection primitive needed) plus a per-day row for full-day
 * vs. hourly toggling. The per-day hourly editing has no shared precedent —
 * it's feature-local by design.
 */
export function DateRangeWithDailyTimeField({ value, onChange }: DateRangeWithDailyTimeFieldProps) {
  const { t } = useTranslation('timeOff');
  const { timeFormat } = useDateFormat();
  const [rangeStart, setRangeStart] = useState<Date | undefined>(
    value[0] ? new Date(`${value[0].date}T00:00:00`) : undefined,
  );
  const [rangeEnd, setRangeEnd] = useState<Date | undefined>(
    value.length > 1 ? new Date(`${value[value.length - 1].date}T00:00:00`) : undefined,
  );

  function handleCalendarSelect(date: Date | undefined) {
    if (!date) return;

    let nextStart = rangeStart;
    let nextEnd = rangeEnd;

    if (!nextStart || (nextStart && nextEnd)) {
      nextStart = date;
      nextEnd = undefined;
    } else if (date < nextStart) {
      nextEnd = nextStart;
      nextStart = date;
    } else {
      nextEnd = date;
    }

    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
    onChange(buildDays(nextStart, nextEnd ?? nextStart, value));
  }

  function updateDay(date: string, patch: Partial<RequestDayFieldValue>) {
    onChange(value.map((day) => (day.date === date ? { ...day, ...patch } : day)));
  }

  return (
    <div className="space-y-4" data-testid="time-off-date-range-field">
      <Calendar rangeStart={rangeStart} rangeEnd={rangeEnd} onSelect={handleCalendarSelect} />

      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((day) => (
            <div
              key={day.date}
              className="flex flex-col gap-2 rounded-md border border-border p-3 md:flex-row md:items-center md:justify-between"
              data-testid={`time-off-day-row-${day.date}`}
            >
              <span className="text-sm font-medium">{formatDisplayDate(day.date)}</span>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <ToggleSwitch
                  id={`full-day-${day.date}`}
                  checked={day.isFullDay}
                  onCheckedChange={(checked) => updateDay(day.date, { isFullDay: checked })}
                  label={t('requests.form.fullDay')}
                />
                {!day.isFullDay && (
                  <div className="flex items-center gap-2">
                    <TimeSelect
                      value={day.startTime || null}
                      timeFormat={timeFormat}
                      onChange={(startTime) => updateDay(day.date, { startTime })}
                    />
                    <span className="text-muted-foreground">–</span>
                    <TimeSelect
                      value={day.endTime || null}
                      timeFormat={timeFormat}
                      onChange={(endTime) => updateDay(day.date, { endTime })}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Builds the day list for a range, preserving per-day state for dates already present. */
function buildDays(
  start: Date,
  end: Date,
  previous: RequestDayFieldValue[],
): RequestDayFieldValue[] {
  const previousByDate = new Map(previous.map((day) => [day.date, day]));
  const days: RequestDayFieldValue[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const dateStr = format(cursor, 'yyyy-MM-dd');
    days.push(
      previousByDate.get(dateStr) ?? {
        date: dateStr,
        isFullDay: true,
        startTime: '',
        endTime: '',
      },
    );
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}
